import React from 'react';

/* ═══════════════════════════════════════════════════════════════
   AmigosWordmark — pure-CSS brand mark, no raster PNG.
   Renders the "AMIGO'S BAIL BONDS" identity as native typography
   so it blends seamlessly with the site (no cream box, no seams).
   ═══════════════════════════════════════════════════════════════ */

const SizePresets = {
  sm: { star: 22, mainSize: '1.05rem', subSize: '0.55rem', gap: '0.5rem', letterSpacing: '0.09em' },
  md: { star: 28, mainSize: '1.35rem', subSize: '0.6rem', gap: '0.65rem', letterSpacing: '0.09em' },
  lg: { star: 34, mainSize: '1.7rem', subSize: '0.68rem', gap: '0.75rem', letterSpacing: '0.09em' },
  xl: { star: 42, mainSize: '2.1rem', subSize: '0.72rem', gap: '0.85rem', letterSpacing: '0.08em' },
};

/**
 * Props:
 *   size: 'sm' | 'md' | 'lg' | 'xl'  (default 'md')
 *   subline: 'tagline' (default) | 'license' | 'both' | null
 *   testId
 */
const AmigosWordmark = ({ size = 'md', subline = 'tagline', testId = 'amigos-wordmark' }) => {
  const p = SizePresets[size] || SizePresets.md;

  return (
    <span className="amg-wordmark" data-testid={testId} style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: p.gap,
      lineHeight: 1,
    }}>
      {/* Custom brass emblem — sun/star with dot cluster (Spanish colonial poster feel) */}
      <span className="amg-wordmark__emblem" aria-hidden="true" style={{
        width: p.star + 4,
        height: p.star + 4,
        flexShrink: 0,
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <svg width={p.star} height={p.star} viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <linearGradient id={`amg-emblem-grad-${size}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#e4b976" />
              <stop offset="55%" stopColor="#b1854a" />
              <stop offset="100%" stopColor="#8f6a30" />
            </linearGradient>
            <filter id={`amg-emblem-glow-${size}`} x="-40%" y="-40%" width="180%" height="180%">
              <feGaussianBlur stdDeviation="1.4" result="blur"/>
              <feMerge>
                <feMergeNode in="blur"/>
                <feMergeNode in="SourceGraphic"/>
              </feMerge>
            </filter>
          </defs>
          {/* Outer 8-point sun/compass */}
          <g filter={`url(#amg-emblem-glow-${size})`}>
            <path
              d="M24 3 L26 18 L38 8 L30 22 L45 24 L30 26 L38 40 L26 30 L24 45 L22 30 L10 40 L18 26 L3 24 L18 22 L10 8 L22 18 Z"
              fill={`url(#amg-emblem-grad-${size})`}
              opacity="0.95"
            />
            {/* Inner concentric ring */}
            <circle cx="24" cy="24" r="7" fill="none" stroke={`url(#amg-emblem-grad-${size})`} strokeWidth="1.4" opacity="0.9" />
            {/* Center dot */}
            <circle cx="24" cy="24" r="2.4" fill="#f0d59a" />
          </g>
        </svg>
      </span>

      <span className="amg-wordmark__text" style={{
        display: 'inline-flex',
        flexDirection: 'column',
        gap: '0.15rem',
        justifyContent: 'center',
      }}>
        <span className="amg-wordmark__main" style={{
          fontFamily: 'var(--font-display)',
          fontSize: p.mainSize,
          letterSpacing: p.letterSpacing,
          textTransform: 'uppercase',
          color: 'var(--text-primary)',
          lineHeight: 0.95,
          textShadow: '0 0 24px rgba(212, 161, 101, 0.22), 0 1px 0 rgba(0,0,0,0.5)',
        }}>
          <span style={{ color: 'var(--gold-warm, #d4a165)' }}>Amigo&rsquo;s</span>
          <span style={{ color: 'var(--text-primary)', marginLeft: '0.35em' }}>Bail Bonds</span>
        </span>
        {subline === 'tagline' && (
          <span className="amg-wordmark__sub" style={{
            fontFamily: 'var(--font-alt)',
            fontSize: p.subSize,
            letterSpacing: '0.24em',
            textTransform: 'uppercase',
            color: 'var(--gold, #b1854a)',
            fontStyle: 'italic',
            fontWeight: 500,
          }}>
            <span style={{ fontStyle: 'normal', letterSpacing: '0.22em' }}>Fianzas de Cárcel</span>
          </span>
        )}
        {subline === 'license' && (
          <span className="amg-wordmark__sub" style={{
            fontFamily: 'var(--font-alt)',
            fontSize: p.subSize,
            letterSpacing: '0.22em',
            textTransform: 'uppercase',
            color: 'var(--gold, #b1854a)',
          }}>
            License #0111 · Conroe, TX
          </span>
        )}
        {subline === 'both' && (
          <span className="amg-wordmark__sub" style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.55rem',
            fontFamily: 'var(--font-alt)',
            fontSize: p.subSize,
            letterSpacing: '0.22em',
            textTransform: 'uppercase',
            color: 'var(--gold, #b1854a)',
          }}>
            <span style={{ letterSpacing: '0.22em' }}>Fianzas de Cárcel</span>
            <span style={{ color: 'var(--text-muted)', opacity: 0.6 }}>·</span>
            <span>License #0111</span>
          </span>
        )}
      </span>
    </span>
  );
};

export default AmigosWordmark;
